import { create } from 'zustand'

// NO_KEY: no usable key/config at all (platform default unavailable).
// PLATFORM_NOT_CONFIGURED: this INSTANCE has no platform AI provider/key, so
//   there is no shared AI to fall back on. Remediation is the user's own key
//   or an instance administrator — never a plan upgrade: no plan can include
//   an AI the instance hasn't configured, and offering "upgrade" to a paid
//   user sends them to buy something they already have.
// QUOTA_EXHAUSTED: monthly platform-key allowance used up.
// CONFIG_NO_CREDENTIAL: the user/tenant HAS defined their own LLM configuration
//   but its credential is missing (a subscription-auth agent with no connected
//   account, or an API config with no key) — remediation is Settings → LLM, not
//   an inline platform key/upgrade.
// KEY_DECRYPT_FAILED: a stored API key EXISTS but can't be decrypted (e.g. it was
//   saved under a since-rotated ENCRYPTION_KEY) — remediation is re-entering the
//   key in Settings → LLM, NOT a plan upgrade (nonsensical for a paid user).
// CREDITS_EXHAUSTED: the credit wallet is empty (any plan, once credit billing
//   is on) — remediation is one of the three ways forward in `options`: buy
//   credits, use the user's own AI key, or switch to a free model. Never a plan
//   upgrade, and never "ask your admin" as the only way out.
// SUBSCRIPTION_LAPSED: the paid plan is past its grace period — remediation is
//   renewing, which is a different page from topping up.
// LIMIT_EXCEEDED / PLAN_APP_LIMIT / PLAN_SEAT_LIMIT / PLAN_REQUIRED: a plan
//   ceiling was hit (records/tables/screens, applications, seats, a feature the
//   plan doesn't include) — remediation is an upgrade.
export type AiAccessCode =
  | 'NO_KEY'
  | 'PLATFORM_NOT_CONFIGURED'
  | 'QUOTA_EXHAUSTED'
  | 'CONFIG_NO_CREDENTIAL'
  | 'KEY_DECRYPT_FAILED'
  | 'CREDITS_EXHAUSTED'
  | 'SUBSCRIPTION_LAPSED'
  | 'LIMIT_EXCEEDED'
  | 'PLAN_APP_LIMIT'
  // The plan's daily agent-turn limit (Basic: 20) — a hardware cap, on any AI.
  | 'DAILY_TURNS_EXHAUSTED'
  | 'PLAN_SEAT_LIMIT'
  | 'PLAN_REQUIRED'

export interface AiAccessError {
  code: AiAccessCode
  detail?: string
  plan?: string | null
  /** Human-readable plan name (e.g. "Free", "Pro") — prefer over `plan`. */
  plan_label?: string | null
  used?: number | null
  limit?: number | null
  /** QUOTA_EXHAUSTED: the allowance is three independent caps (input tokens,
   *  output tokens, spend) and `used`/`limit` above are only the combined
   *  TOKEN pair. When spend is what tripped — which on an agent-mode account
   *  is the only cap that realistically can, since cache-read tokens carry the
   *  cost but count toward no token dimension — that pair sits far below its
   *  limit and contradicts the refusal. These name the cap that actually
   *  tripped, with its own numbers, in the unit it is bought in. */
  exceeded?: string[] | null
  dimension?: string | null
  dimension_used?: number | null
  dimension_limit?: number | null
  dimension_unit?: 'credits' | 'tokens' | null
  /** Credit-wallet balance and the amount the blocked action needed. */
  balance?: number | null
  required?: number | null
  /** Which wallet was checked: the user's own or their organization's. */
  scope?: 'user' | 'tenant' | 'own' | null
  /** For plan-ceiling codes: the current count against the limit. */
  current?: number | null
  /** DAILY_TURNS_EXHAUSTED: when today's turns come back (ISO, UTC midnight). */
  resets_at?: string | null
  /** DAILY_TURNS_EXHAUSTED: which allowance ran out — AI credits or own AI. */
  turn_kind?: 'credit' | 'own' | null
  /** CREDITS_EXHAUSTED / QUOTA_EXHAUSTED: which ways forward this instance
   *  can actually offer (server-computed — see usage_billing.out_of_credits_options). */
  options?: AiAccessOptions | null
  /** A refusal that arrived over an agent WebSocket has no request to replay;
   *  it carries what to send again instead, so the dock that mounts after the
   *  user fixes it (possibly on a different AI engine) can carry on. */
  resend?: PendingResend | null
  /** Replay the original failed request and resolve the caller's awaited
   *  promise with the result — gives seamless continuation after the user
   *  adds a key. Provided by the interceptor. */
  retry?: () => void
  /** Reject the held request (user dismissed / chose to upgrade later). */
  cancel?: () => void
}

export interface AiAccessOptions {
  packs_available?: boolean
  payments_ready?: boolean
  free_model?: { id: string; label: string } | null
}

/** What to send again once the user has bought credits / set up their AI. */
export type PendingResend =
  | { project: string; kind: 'task'; taskType: string; taskData: Record<string, unknown> }
  | { project: string; kind: 'resume' }

interface AiAccessState {
  error: AiAccessError | null
  setError: (e: AiAccessError) => void
  /** Internal: drop the modal without touching the held request. */
  reset: () => void
  /** One-shot hand-off from the modal to the dock that mounts next. */
  pendingResend: PendingResend | null
  setPendingResend: (r: PendingResend | null) => void
}

export const useAiAccessStore = create<AiAccessState>((set) => ({
  error: null,
  setError: (e) => set({ error: e }),
  reset: () => set({ error: null }),
  pendingResend: null,
  setPendingResend: (r) => set({ pendingResend: r }),
}))

/** Claim the pending resend for `project` if it is of one of `kinds`; it is
 *  cleared so exactly one dock acts on it. */
export function takePendingResend<K extends PendingResend['kind']>(
  project: string, kinds: K[],
): Extract<PendingResend, { kind: K }> | null {
  const cur = useAiAccessStore.getState().pendingResend
  if (!cur || cur.project !== project || !kinds.includes(cur.kind as K)) return null
  useAiAccessStore.getState().setPendingResend(null)
  return cur as Extract<PendingResend, { kind: K }>
}

/** Imperative setter usable from the (non-React) axios interceptor. */
export function reportAiAccessError(e: AiAccessError) {
  useAiAccessStore.getState().setError(e)
}

/** Every field the server may put on an ai_access refusal.
 *
 *  One list, because there are five places that turn a server payload into an
 *  `AiAccessError` — the axios 402 interceptor and the `ai_access_error`
 *  WebSocket event in each of the three agent hooks — and each used to copy the
 *  fields out by hand. A field the server started sending therefore reached the
 *  modal only in whichever site was remembered, which is how a refusal kept
 *  rendering the combined token pair after the backend had begun sending the
 *  cap that actually tripped: the server was right and all five readers threw
 *  the answer away. Adding a field here reaches every surface at once. */
const AI_ACCESS_FIELDS = [
  'code', 'detail', 'plan', 'plan_label', 'used', 'limit',
  'balance', 'required', 'scope', 'current',
  'exceeded', 'dimension', 'dimension_used', 'dimension_limit', 'dimension_unit',
  'options', 'resets_at', 'turn_kind',
] as const

/** Lift an ai_access payload — an HTTP 402 envelope or a WS event — into the
 *  modal's shape, keeping only what the server actually sent. */
export function pickAiAccessFields(raw: Record<string, unknown>): AiAccessError {
  const out: Record<string, unknown> = {}
  for (const key of AI_ACCESS_FIELDS) {
    if (raw[key] !== undefined) out[key] = raw[key]
  }
  // Agent sockets name the human text `message`; HTTP calls it `detail`.
  if (out.detail == null && raw.message != null) out.detail = raw.message
  if (!out.code) out.code = 'QUOTA_EXHAUSTED'
  return out as unknown as AiAccessError
}
